import { useCallback } from 'react'
import type { HotelMapViewData, ExperienceMapViewData, OfferMapSuggestionData, TripStop, TripItineraryItem, TripItemWithoutDay, ManualItineraryMovePlacementIntent } from 'tripPlanner/api/types'
import type { DrawerHotel } from '../components/HotelListingDrawer'
import TripPlanCanvas from '../components/TripPlanCanvas'
import type { TimelineMove } from '../components/tripPlanCanvasReorder'
import DayItineraryCanvas from '../components/DayItineraryCanvas'
import { DayItineraryOverlay } from '../styles/layoutStyles'
import { useTripPlannerEventBus } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { useItineraryFingerprint } from '../hooks/useItineraryFingerprint'
import { useChatActions } from '../contexts/ChatActionsContext'
import { useItineraryContext } from '../contexts/ItineraryContext'
import noop from 'lib/function/noop'
import { buildPlaceDrawerOpenedActivityEntry } from './buildPlaceDrawerOpenedActivityEntry'

interface Props {
  /** When false, hooks still run (preserving fingerprint tracking) but JSX returns null */
  visible: boolean
  selectedDayItineraryId: number | undefined
  selectedStop: TripStop | undefined
  surface?: 'default' | 'mobile'
  enableMapActions?: boolean
  onAskAIAction?: () => void
  drawerCloseSignal?: number
  onDrawerOpenChange?: (isOpen: boolean) => void
}

function toManualItineraryMovePlacementIntent(placement: TimelineMove['placement']): ManualItineraryMovePlacementIntent {
  return placement
}

function ItineraryContainer({
  visible,
  selectedDayItineraryId,
  selectedStop,
  surface = 'default',
  enableMapActions = true,
  onAskAIAction = noop,
  drawerCloseSignal,
  onDrawerOpenChange,
}: Props) {
  const eventBus = useTripPlannerEventBus()
  const { sendMessage } = useChatActions()
  const {
    itineraryData,
    hasItineraryData,
    enrichingStopIds,
    scrollTarget,
    clearScrollTarget,
    refetchCurrentChatItinerary,
    deleteManualItineraryTarget,
    moveManualItineraryItems,
  } = useItineraryContext()

  // ── Fingerprint tracking (dispatches 'itinerary_changed' on structural changes) ──

  useItineraryFingerprint({
    itineraryData,
    hasItineraryData,
    dispatch: eventBus.dispatch,
  })

  // ── Callback wrappers dispatching cross-container events ──

  const handleLearnMoreClick = useCallback((data: { googlePlaceId: string; placeName: string; countryName?: string; stopId?: number }) => {
    eventBus.dispatch({ type: 'open_detail_drawer', ...data })
    eventBus.dispatch({
      type: 'trip_planner_surface_activity',
      entries: [buildPlaceDrawerOpenedActivityEntry(data.placeName, 'trip_planner.itinerary.learn_more', data.googlePlaceId)],
    })
  }, [eventBus])

  const dispatchOfferSuggestion = useCallback((data: OfferMapSuggestionData) => {
    eventBus.dispatch({ type: 'show_offer_suggestion_on_map', data })
  }, [eventBus])

  const handleShowMap = useCallback((data: HotelMapViewData) => {
    const offer = data.offers.find(item => item.coordinates)
    if (!offer?.coordinates) return
    dispatchOfferSuggestion({
      offerId: offer.id,
      name: offer.name,
      coordinates: offer.coordinates,
      offerType: 'hotel',
    })
  }, [dispatchOfferSuggestion])

  const handleShowExperienceOnMap = useCallback((data: ExperienceMapViewData) => {
    const offer = data.offers.find(item => item.coordinates)
    if (!offer?.coordinates) return
    dispatchOfferSuggestion({
      offerId: offer.id,
      name: offer.name,
      coordinates: offer.coordinates,
      offerType: 'experience',
    })
  }, [dispatchOfferSuggestion])

  const handleAskAIForDay = useCallback((dayNumber: number, dayTitle: string, date?: string) => {
    onAskAIAction()
    eventBus.dispatch({ type: 'ask_ai_for_day', dayNumber, title: dayTitle, date })
  }, [eventBus, onAskAIAction])

  const handleAskAIForItem = useCallback((item: TripItineraryItem | TripItemWithoutDay) => {
    onAskAIAction()
    eventBus.dispatch({ type: 'ask_ai_for_item', itemId: item.id, description: item.description, name: item.name, category: item.itemPlaceDetail?.primaryTypeDisplayName })
  }, [eventBus, onAskAIAction])

  const handleOfferShowMap = useCallback((hotel: DrawerHotel) => {
    if (!hotel.geoLocation) return
    dispatchOfferSuggestion({
      offerId: hotel.id,
      name: hotel.name,
      coordinates: hotel.geoLocation,
      offerType: 'hotel',
    })
  }, [dispatchOfferSuggestion])

  const handleMoveItem = useCallback((itemId: number, move: TimelineMove) => {
    return moveManualItineraryItems({
      moves: [{ itemId, target: move.target, placement: toManualItineraryMovePlacementIntent(move.placement) }],
    })
  }, [moveManualItineraryItems])

  // ── Render ──

  // Convention exception: "Components should not determine whether they render"
  // This container must stay mounted so the fingerprint tracking effect detects
  // itinerary changes regardless of which view is active. The parent controls
  // visibility via the `visible` prop; the child only gates JSX output.
  if (!visible) return null

  const stopId = selectedStop?.id
  const shouldShowDayItinerary = selectedDayItineraryId && selectedStop && selectedStop.days.length > 0
  const showHotelMapHandler = enableMapActions ? handleShowMap : undefined
  const showExperienceMapHandler = enableMapActions ? handleShowExperienceOnMap : undefined
  const showOfferMapHandler = enableMapActions ? handleOfferShowMap : undefined

  return (
    <>
      <TripPlanCanvas
        data={itineraryData}
        onLearnMoreClick={handleLearnMoreClick}
        onRefetchItinerary={refetchCurrentChatItinerary}
        onSendMessage={sendMessage}
        onDeleteTarget={deleteManualItineraryTarget}
        onShowMap={showHotelMapHandler}
        onShowExperienceOnMap={showExperienceMapHandler}
        enrichingStopIds={enrichingStopIds}
        onAskAI={handleAskAIForDay}
        onAskAIForItem={handleAskAIForItem}
        onMoveItem={handleMoveItem}
        scrollTarget={scrollTarget}
        onScrollComplete={clearScrollTarget}
        drawerCloseSignal={drawerCloseSignal}
        onDrawerOpenChange={onDrawerOpenChange}
        stopTimingEditVariant={surface === 'mobile' ? 'mobile' : 'default'}
      />
      {shouldShowDayItinerary && (
        <DayItineraryOverlay>
          <DayItineraryCanvas
            data={itineraryData}
            stopId={stopId}
            onSendMessage={sendMessage}
            onDeleteTarget={deleteManualItineraryTarget}
            onShowMap={showOfferMapHandler}
            onShowExperienceOnMap={showExperienceMapHandler}
            onAskAIForItem={handleAskAIForItem}
          />
        </DayItineraryOverlay>
      )}
    </>
  )
}

export default ItineraryContainer
